import React, { useEffect, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Pencil, Plug, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import McpServerDialog from './McpServerDialog';
import { AUTH_MODE_LABELS, McpServer, errorDetail, useMcpServers } from './mcpServerRegistry';

interface Usage {
  agents: string[];
  services: string[];
  total: number;
}

/**
 * Integrations → MCP Servers: the org-level registry (MCP_SERVERS_PLAN.md).
 * Rename, replace key, re-discover, disable, delete — every change reaches
 * every agent and service bound to the server; disable/delete warn with the
 * bound-consumer list first.
 */
const McpServers: React.FC = () => {
  const { get, put, post, remove } = useApi();
  const { servers, loading, error, upsert, removeLocal, refresh } = useMcpServers();

  // Back from an OAuth sign-in (the backend callback redirects here with a
  // result marker). Report it once and strip the params so a reload is quiet.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('mcp_oauth');
    if (!result) return;
    if (result === 'connected') {
      toast.success('MCP server signed in and connected');
      refresh();
    } else {
      toast.error(params.get('message') || 'Sign-in to the MCP server did not complete.');
    }
    const url = new URL(window.location.href);
    ['mcp_oauth', 'mcp_server_id', 'message'].forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<McpServer | null>(null);
  const [rediscovering, setRediscovering] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<
    { kind: 'delete' | 'disable'; server: McpServer; usage: Usage } | null
  >(null);
  const [busy, setBusy] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (server: McpServer) => {
    setEditing(server);
    setDialogOpen(true);
  };

  const handleSaved = (server: McpServer) => {
    upsert(server);
    toast.success(editing ? 'MCP server updated' : 'MCP server connected');
  };

  const handleRediscover = async (server: McpServer) => {
    setRediscovering(server.id);
    try {
      const response = await post(`/mcp-servers/${server.id}/rediscover`, {});
      upsert(response.data);
      toast.success(`Discovered ${response.data.tools?.length ?? 0} tools on ${server.name}`);
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not re-discover tools.'));
    } finally {
      setRediscovering(null);
    }
  };

  const loadUsage = async (server: McpServer): Promise<Usage> => {
    const response = await get(`/mcp-servers/${server.id}/usage`);
    return response.data as Usage;
  };

  const requestToggleEnabled = async (server: McpServer, enabled: boolean) => {
    if (enabled) {
      await applyEnabled(server, true);
      return;
    }
    try {
      const usage = await loadUsage(server);
      if (usage.total === 0) {
        await applyEnabled(server, false);
        return;
      }
      setPendingAction({ kind: 'disable', server, usage });
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not check where this server is used.'));
    }
  };

  const applyEnabled = async (server: McpServer, enabled: boolean) => {
    setBusy(true);
    try {
      const response = await put(`/mcp-servers/${server.id}`, { enabled });
      upsert(response.data);
      toast.success(enabled ? `${server.name} enabled` : `${server.name} disabled`);
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not update MCP server.'));
    } finally {
      setBusy(false);
      setPendingAction(null);
    }
  };

  const requestDelete = async (server: McpServer) => {
    try {
      const usage = await loadUsage(server);
      setPendingAction({ kind: 'delete', server, usage });
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not check where this server is used.'));
    }
  };

  const applyDelete = async (server: McpServer) => {
    setBusy(true);
    try {
      await remove(`/mcp-servers/${server.id}`);
      removeLocal(server.id);
      toast.success(`${server.name} removed`);
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not delete MCP server.'));
    } finally {
      setBusy(false);
      setPendingAction(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Plug className="h-5 w-5" /> MCP Servers
              </CardTitle>
              <CardDescription className="mt-1">
                Connect your product&apos;s MCP servers once for the organization. Any agent or
                service can then be given a subset of a server&apos;s tools. Credentials are stored
                here only — never in agent configuration, versions, or runs — so rotating a key
                takes effect everywhere at once.
              </CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={openCreate}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Connect server
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : error ? (
            <p className="text-sm text-red-500">{error}</p>
          ) : servers.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
              <p className="text-sm font-medium">No MCP servers connected</p>
              <p className="text-xs text-muted-foreground max-w-lg mx-auto">
                Connect a server that speaks the Model Context Protocol. You can also connect one
                directly from an agent&apos;s MCP Servers tab or a service&apos;s tool settings.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={openCreate}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Connect server
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {servers.map((server) => (
                <div key={server.id} className="rounded-lg border p-4 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold break-words [overflow-wrap:anywhere]">{server.name}</span>
                        <Badge variant="secondary" className="text-xs font-normal">
                          {AUTH_MODE_LABELS[server.auth_mode]}
                        </Badge>
                        {server.auth_mode === 'api_key' && (
                          <Badge
                            variant="outline"
                            className={`text-xs font-normal ${server.has_credential ? '' : 'text-amber-600 border-amber-300'}`}
                          >
                            {server.has_credential ? 'Key stored' : 'No key'}
                          </Badge>
                        )}
                        {server.auth_mode === 'oauth' && server.oauth?.status === 'needs_reauth' && (
                          <Badge variant="outline" className="text-xs font-normal text-amber-600 border-amber-300">
                            Needs re-authorization
                          </Badge>
                        )}
                        {!server.enabled && (
                          <Badge variant="outline" className="text-xs font-normal text-amber-600 border-amber-300">
                            Disabled
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground truncate font-mono">{server.url}</p>
                      {server.description && (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{server.description}</p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {server.tools.length} tool{server.tools.length === 1 ? '' : 's'}
                        {server.tools_discovered_at
                          ? ` · discovered ${formatDate(server.tools_discovered_at)}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Switch
                        checked={server.enabled}
                        disabled={busy}
                        onCheckedChange={(checked) => requestToggleEnabled(server, checked)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground"
                        title="Re-discover tools"
                        disabled={rediscovering === server.id}
                        onClick={() => handleRediscover(server)}
                      >
                        {rediscovering === server.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground"
                        title="Edit"
                        onClick={() => openEdit(server)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        title="Delete"
                        onClick={() => requestDelete(server)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {server.tools.length > 0 && (
                    <p className="text-xs text-muted-foreground font-mono break-words">
                      {server.tools
                        .slice(0, 12)
                        .map((tool) => tool.name)
                        .join(' · ')}
                      {server.tools.length > 12 ? ` · +${server.tools.length - 12} more` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <McpServerDialog open={dialogOpen} onOpenChange={setDialogOpen} server={editing} onSaved={handleSaved} />

      <Dialog open={Boolean(pendingAction)} onOpenChange={(open) => !open && !busy && setPendingAction(null)}>
        <DialogContent className="sm:max-w-md">
          {pendingAction && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {pendingAction.kind === 'delete' ? 'Delete' : 'Disable'} {pendingAction.server.name}?
                </DialogTitle>
                <DialogDescription>
                  {pendingAction.usage.total === 0
                    ? 'Nothing is using this server.'
                    : `${pendingAction.usage.total} ${pendingAction.usage.total === 1 ? 'consumer uses' : 'consumers use'} this server. Their tools from it will ${
                        pendingAction.kind === 'delete' ? 'disappear' : 'stop working until it is re-enabled'
                      }.`}
                </DialogDescription>
              </DialogHeader>
              {pendingAction.usage.total > 0 && (
                <div className="text-xs text-muted-foreground space-y-1">
                  {pendingAction.usage.agents.length > 0 && (
                    <p>
                      <span className="font-medium text-foreground">Agents:</span>{' '}
                      {pendingAction.usage.agents.join(', ')}
                    </p>
                  )}
                  {pendingAction.usage.services.length > 0 && (
                    <p>
                      <span className="font-medium text-foreground">Services:</span>{' '}
                      {pendingAction.usage.services.join(', ')}
                    </p>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" disabled={busy} onClick={() => setPendingAction(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={busy}
                  onClick={() =>
                    pendingAction.kind === 'delete'
                      ? applyDelete(pendingAction.server)
                      : applyEnabled(pendingAction.server, false)
                  }
                >
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  {pendingAction.kind === 'delete' ? 'Delete' : 'Disable'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default McpServers;
